import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Landmark, Link2, PiggyBank } from "lucide-react";

import { api } from "@/api/client";
import type {
  BankLinkOut,
  LeaseOut,
  LeaseState,
  Pool,
  Replica,
  Service,
} from "@/api/types";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";
import { useAllPools, useAllReplicas, useServices } from "@/workloads/data";
import type { Focus } from "@/workloads/focus";

/**
 * Bank page (capacity-bank-pod-lease.md): pre-acquired GPU pods held as
 * lendable inventory. The one rule everything here communicates: pods
 * are NEVER terminated by lease operations — capacity moves between
 * services by switching the workload inside the running pod.
 *
 * The bank itself is a singleton owned by the controller (seeded by
 * ``controller.leases.preset``, immune to rename / disable / delete),
 * so this page never offers to create one. What the operator configures here is
 * the inventory: image, pools, scale, and who may borrow.
 */

const LEASE_TONE: Record<LeaseState, PillTone> = {
  assigning: "info",
  active: "ok",
  draining: "warn",
  released: "neutral",
  failed: "danger",
};

interface Props {
  setFocusAndTab?: (focus: Focus, tab: string) => void;
}

const LEASE_INFO: Record<LeaseState, string> = {
  assigning:
    "The in-pod agent is running the lease's setup + boot; routing starts once the workload passes its health check.",
  active: "Serving the lessee's traffic (routed by Envoy on the app port).",
  draining:
    "Being handed back: routing already removed, in-flight requests draining, then the workload process is stopped. The pod stays alive.",
  released: "Loan finished; the pod is idle bank inventory again.",
  failed:
    "The assignment failed or the workload exited; the orchestrator returns the pod to inventory. The pod itself is unaffected.",
};

export function GlobalBank({ setFocusAndTab }: Props) {
  const qc = useQueryClient();
  const services = useServices();
  const bankServices = (services.data ?? []).filter((s) => s.kind === "bank");
  const workloadServices = (services.data ?? []).filter(
    (s) => s.kind !== "bank",
  );
  const pools = useAllPools(bankServices.map((s) => s.id));
  const replicas = useAllReplicas();
  const leases = useQuery({
    queryKey: ["leases"],
    queryFn: () => api.get<LeaseOut[]>("/leases?include_history=false"),
    refetchInterval: 8_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["leases"] });
    void qc.invalidateQueries({ queryKey: ["bank-links"] });
  };

  if (services.data && bankServices.length === 0) {
    return (
      <Section title="Capacity bank" icon={<PiggyBank />}>
        <p className="muted">
          BackStage has exactly one capacity bank and the controller
          seeds it on startup — it can't be created, renamed or deleted
          by hand. Not seeing it here means the seed didn't run: check
          the controller logs for <code>bank.preset.*</code>.
        </p>
      </Section>
    );
  }

  return (
    <>
      {bankServices.map((svc) => (
        <BankServiceCard
          key={svc.id}
          service={svc}
          setFocusAndTab={setFocusAndTab}
          pools={(pools.data ?? []).filter((p) => p.service_id === svc.id)}
          replicas={replicas.data ?? []}
          leases={leases.data ?? []}
          workloadServices={workloadServices}
          onChanged={invalidate}
        />
      ))}
    </>
  );
}

function BankServiceCard({
  service,
  pools,
  replicas,
  leases,
  workloadServices,
  onChanged,
  setFocusAndTab,
}: {
  service: Service;
  pools: Pool[];
  replicas: Replica[];
  leases: LeaseOut[];
  workloadServices: Service[];
  onChanged: () => void;
  setFocusAndTab?: (focus: Focus, tab: string) => void;
}) {
  if (!pools.length) {
    return (
      <Section
        title={service.display_name ?? service.name}
        icon={<PiggyBank />}
        count="no pools yet"
      >
        <p className="muted">
          This bank holds no inventory yet. Give it the image family you
          want the banked pods to run (leased workloads run <em>inside</em>{" "}
          the pod, so the image has to carry their runtime), then add a
          pool with the GPU shape to stockpile (the <code>+</code> on this
          service's row in the left tree) and scale it up. Once pods
          are up, link the services allowed to borrow — handovers (e.g.
          tts4 ⇄ loadtest) switch the workload inside the running pod;
          the pod itself is never released to the provider.
        </p>
        {setFocusAndTab && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="btn btn--sm"
              onClick={() =>
                setFocusAndTab({ type: "service", id: service.id }, "workload")
              }
            >
              set the bank image
            </button>
            <button
              className="btn btn--sm"
              onClick={() =>
                setFocusAndTab({ type: "service", id: service.id }, "dashboard")
              }
            >
              open the bank
            </button>
          </div>
        )}
      </Section>
    );
  }
  return (
    <>
      {pools.map((pool) => (
        <BankPoolSection
          key={pool.id}
          service={service}
          pool={pool}
          replicas={replicas.filter((r) => r.pool_id === pool.id)}
          leases={leases.filter((l) => l.bank_pool_id === pool.id)}
          workloadServices={workloadServices}
          onChanged={onChanged}
        />
      ))}
    </>
  );
}

function BankPoolSection({
  service,
  pool,
  replicas,
  leases,
  workloadServices,
  onChanged,
}: {
  service: Service;
  pool: Pool;
  replicas: Replica[];
  leases: LeaseOut[];
  workloadServices: Service[];
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const links = useQuery({
    queryKey: ["bank-links", pool.id],
    queryFn: () => api.get<BankLinkOut[]>(`/pools/${pool.id}/bank-links`),
    refetchInterval: 10_000,
  });
  const serviceName = (id: number) =>
    workloadServices.find((s) => s.id === id)?.display_name ??
    workloadServices.find((s) => s.id === id)?.name ??
    `service ${id}`;

  const liveLeaseByReplica = new Map(
    leases
      .filter((l) => l.state !== "released" && l.state !== "failed")
      .map((l) => [l.replica_id, l] as const),
  );
  const idle = replicas.filter(
    (r) => r.status === "ready" && !liveLeaseByReplica.has(r.id),
  ).length;

  return (
    <Section
      title={`${service.display_name ?? service.name} / ${
        pool.display_name ?? pool.sky_service_name
      }`}
      icon={<Landmark />}
      count={
        <>
          {replicas.length} pods · {idle} idle ·{" "}
          {pool.accelerators ?? "no shape"}
        </>
      }
      flush
    >
      {pool.bank_spec_apply_deferred && (
        <div className="flash" style={{ margin: "0 0 8px" }}>
          <strong>Pod spec update pending.</strong> This bank's rendered
          spec changed — usually because BackStage itself was upgraded
          (the in-pod agent ships inside the pod's YAML) or the egress
          IPs rotated. Applying it would make SkyServe replace the
          banked pods, so it is being held back. It applies by itself
          once this bank's inventory reaches zero; until then the pods
          keep running the agent they booted with.
        </div>
      )}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Pod</th>
              <th>Status</th>
              <th>Where</th>
              <th>On loan to</th>
              <th>Lease</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {replicas.map((r) => {
              const lease = liveLeaseByReplica.get(r.id);
              return (
                <tr key={r.id} className="hoverable">
                  <td className="mono">
                    {r.sky_cluster_name ?? `replica ${r.id}`}
                  </td>
                  <td>
                    <Pill tone={r.status === "ready" ? "ok" : "neutral"}>
                      {r.status}
                    </Pill>
                  </td>
                  <td className="muted">
                    {[r.cloud, r.region].filter(Boolean).join(" / ") || "—"}
                  </td>
                  <td>
                    {lease ? (
                      serviceName(lease.service_id)
                    ) : (
                      <span className="muted">idle (banked)</span>
                    )}
                  </td>
                  <td>
                    {lease ? (
                      <Pill
                        tone={LEASE_TONE[lease.state]}
                        title={
                          LEASE_INFO[lease.state] +
                          (lease.failure_reason
                            ? ` — ${lease.failure_reason}`
                            : "")
                        }
                      >
                        {lease.state}
                      </Pill>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {lease && (
                      <LeaseActions
                        lease={lease}
                        pool={pool}
                        links={links.data ?? []}
                        serviceName={serviceName}
                        onChanged={() => {
                          onChanged();
                          void qc.invalidateQueries({
                            queryKey: ["bank-links", pool.id],
                          });
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            {!replicas.length && (
              <tr>
                <td colSpan={6} className="empty">
                  no inventory yet — scale this bank pool up to stockpile
                  pods (they idle on the bank agent until leased)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <LinksTable
        pool={pool}
        links={links.data ?? []}
        idle={idle}
        workloadServices={workloadServices}
        serviceName={serviceName}
        onChanged={() => {
          onChanged();
          void qc.invalidateQueries({ queryKey: ["bank-links", pool.id] });
        }}
      />
    </Section>
  );
}

function LeaseActions({
  lease,
  pool,
  links,
  serviceName,
  onChanged,
}: {
  lease: LeaseOut;
  pool: Pool;
  links: BankLinkOut[];
  serviceName: (id: number) => string;
  onChanged: () => void;
}) {
  const [target, setTarget] = useState<number | "">("");
  const release = useMutation({
    mutationFn: () => api.post<LeaseOut>(`/leases/${lease.id}/release`),
    onSuccess: onChanged,
    onError: (e: Error) => window.alert(e.message),
  });
  const reassign = useMutation({
    mutationFn: (serviceId: number) =>
      api.post<LeaseOut>(`/leases/${lease.id}/reassign`, {
        target_service_id: serviceId,
      }),
    onSuccess: onChanged,
    onError: (e: Error) => window.alert(e.message),
  });
  if (lease.state !== "active" && lease.state !== "assigning") return null;
  const otherLinks = links.filter(
    (l) => l.enabled && l.service_id !== lease.service_id,
  );
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <button
        className="btn btn--sm"
        disabled={release.isPending}
        title="Drain and hand the pod back to the bank (pod stays alive)"
        onClick={() => release.mutate()}
      >
        release
      </button>
      {otherLinks.length > 0 && (
        <>
          <select
            className="field"
            value={target}
            onChange={(e) =>
              setTarget(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <option value="">move to…</option>
            {otherLinks.map((l) => (
              <option key={l.id} value={l.service_id}>
                {serviceName(l.service_id)}
              </option>
            ))}
          </select>
          <button
            className="btn btn--sm"
            disabled={target === "" || reassign.isPending}
            title={`Hand this capacity to another service without terminating the pod (bank pool ${pool.id})`}
            onClick={() => target !== "" && reassign.mutate(target)}
          >
            <ArrowLeftRight style={{ width: 12, height: 12 }} />
          </button>
        </>
      )}
    </div>
  );
}

function LinksTable({
  pool,
  links,
  idle,
  workloadServices,
  serviceName,
  onChanged,
}: {
  pool: Pool;
  links: BankLinkOut[];
  idle: number;
  workloadServices: Service[];
  serviceName: (id: number) => string;
  onChanged: () => void;
}) {
  const [newService, setNewService] = useState<number | "">("");
  const create = useMutation({
    mutationFn: (serviceId: number) =>
      api.post<BankLinkOut>(`/pools/${pool.id}/bank-links`, {
        service_id: serviceId,
        priority: -1,
      }),
    onSuccess: () => {
      setNewService("");
      onChanged();
    },
    onError: (e: Error) => window.alert(e.message),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: unknown }) =>
      api.patch<BankLinkOut>(`/bank-links/${id}`, body),
    onSuccess: onChanged,
    onError: (e: Error) => window.alert(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/bank-links/${id}`),
    onSuccess: onChanged,
    onError: (e: Error) => window.alert(e.message),
  });
  const unlinked = workloadServices.filter(
    (s) => !links.some((l) => l.service_id === s.id),
  );
  return (
    <div className="table-wrap" style={{ marginTop: 8 }}>
      <table className="data">
        <thead>
          <tr>
            <th>
              <Link2
                style={{ width: 13, height: 13, verticalAlign: "middle" }}
              />{" "}
              Lendable to
            </th>
            <th title="Position inside the lessee's pool cascade; negative sits in front of P0 = borrow before launching fresh replicas">
              Priority
            </th>
            <th title="How many of this bank's pods the service should hold. manual = operator-pinned; autoscaler = demand-driven">
              Desired
            </th>
            <th>Held</th>
            <th>Source</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {links.map((link) => (
            <tr key={link.id} className="hoverable">
              <td>{serviceName(link.service_id)}</td>
              <td className="mono">{link.priority}</td>
              <td className="mono">{link.desired_count}</td>
              <td className="mono">
                {link.active_leases}
                {link.assigning_leases > 0 && ` (+${link.assigning_leases})`}
              </td>
              <td>
                <Pill tone={link.source === "manual" ? "brand" : "neutral"}>
                  {link.source}
                </Pill>
              </td>
              <td>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    className="btn btn--sm"
                    title="Lease one more banked pod to this service (manual pin)"
                    disabled={patch.isPending || idle <= 0}
                    onClick={() =>
                      patch.mutate({
                        id: link.id,
                        body: { desired_count: link.desired_count + 1 },
                      })
                    }
                  >
                    +1
                  </button>
                  <button
                    className="btn btn--sm"
                    title="Give one pod back to the bank (manual pin)"
                    disabled={patch.isPending || link.desired_count <= 0}
                    onClick={() =>
                      patch.mutate({
                        id: link.id,
                        body: { desired_count: link.desired_count - 1 },
                      })
                    }
                  >
                    −1
                  </button>
                  {link.source === "manual" && (
                    <button
                      className="btn btn--sm"
                      title="Hand the count back to the autoscaler"
                      disabled={patch.isPending}
                      onClick={() =>
                        patch.mutate({
                          id: link.id,
                          body: { resume_autoscaler: true },
                        })
                      }
                    >
                      auto
                    </button>
                  )}
                  <button
                    className="btn btn--sm btn--danger"
                    title="Remove lendability (refused while leases are live)"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(link.id)}
                  >
                    unlink
                  </button>
                </div>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={6}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  className="field"
                  value={newService}
                  onChange={(e) =>
                    setNewService(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                >
                  <option value="">link a service…</option>
                  {unlinked.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name ?? s.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn--sm"
                  disabled={newService === "" || create.isPending}
                  onClick={() => newService !== "" && create.mutate(newService)}
                >
                  allow borrowing
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
